import React, { useState, useEffect, useMemo, Fragment, useContext, useCallback } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Badge, Collapse, Card, Row, Col, Spinner, Pagination } from 'react-bootstrap';
import api from '../../api/axios';
import AuthContext from '../../context/AuthContext';
import SettingsContext from '../../context/SettingsContext';
import { hasPermission } from '../../utils/permissions';
import { formatDateTime } from '../../utils/format';

const ManageVehicles = () => {
    const { user } = useContext(AuthContext);
    const { settings } = useContext(SettingsContext);
    const location = useLocation();

    const [vehicles, setVehicles] = useState([]);
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1 });
    const [isLoading, setIsLoading] = useState(false);
    
    const [carModels, setCarModels] = useState([]);
    const [vehicleSizes, setVehicleSizes] = useState([]);
    
    const [filters, setFilters] = useState({ plateNumberSearch: '', modelSearch: '' });

    const [showModal, setShowModal] = useState(false);
    const [currentVehicle, setCurrentVehicle] = useState({});
    const [selectedManufacturer, setSelectedManufacturer] = useState('');
    const [selectedCarModelId, setSelectedCarModelId] = useState('');
    const [selectedVehicleSizeId, setSelectedVehicleSizeId] = useState('');
    const [comment, setComment] = useState('');

    const [expandedRows, setExpandedRows] = useState({});
    const [serviceHistories, setServiceHistories] = useState({});

    const fetchVehicles = useCallback(async (page = 1) => {
        if (!user) return;
        setIsLoading(true);
        try {
            const params = { ...filters, page, limit: 20 };
            const response = await api.get('/vehicles', { params });
            setVehicles(response.data.data.vehicles);
            setPagination({
                currentPage: response.data.data.currentPage,
                totalPages: response.data.data.totalPages,
            });
        } catch (error) {
            console.error("Error fetching vehicles:", error);
        } finally {
            setIsLoading(false);
        }
    }, [user, filters]);

    useEffect(() => {
        if (user) {
            fetchVehicles(1);
            api.get('/carmodels').then(res => setCarModels(res.data.data.filter(m => m.isActive)));
            api.get('/vehiclesizes').then(res => setVehicleSizes(res.data.data.sizes));
        }
    }, [user, fetchVehicles]);

    useEffect(() => {
        if (location.state?.initialSearch) {
            setFilters(prev => ({ ...prev, plateNumberSearch: location.state.initialSearch }));
        }
    }, [location.state]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchVehicles(1);
    };

    const handlePageChange = (pageNumber) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            fetchVehicles(pageNumber);
        }
    };

    const uniqueManufacturers = useMemo(() => {
        // API가 displayOrder 순으로 반환 → 등장 순서 유지
        const seen = new Set();
        const list = [];
        carModels.forEach(m => {
            if (m.manufacturer && !seen.has(m.manufacturer)) {
                seen.add(m.manufacturer);
                list.push(m.manufacturer);
            }
        });
        return ['', ...list];
    }, [carModels]);

    const modelsByManufacturer = useMemo(() => {
        if (!selectedManufacturer) return [];
        return carModels.filter(m => m.manufacturer === selectedManufacturer).sort((a, b) => a.displayOrder - b.displayOrder || a.modelName.localeCompare(b.modelName));
    }, [carModels, selectedManufacturer]);

    const toggleRow = async (vehicleId, plateNumber) => {
        if (!user) return;
        const isExpanded = !!expandedRows[vehicleId];
        setExpandedRows(prev => ({ ...prev, [vehicleId]: !isExpanded }));

        if (!isExpanded && !serviceHistories[vehicleId]) {
            try {
                setServiceHistories(prev => ({ ...prev, [vehicleId]: 'loading' }));
                const response = await api.get(`/vehicles/${plateNumber}`);
                setServiceHistories(prev => ({ ...prev, [vehicleId]: response.data.data.serviceHistory || [] }));
            } catch (error) {
                console.error("Error fetching service history:", error);
                setServiceHistories(prev => ({ ...prev, [vehicleId]: [] }));
            }
        }
    };

    const handleShowModal = (vehicle) => {
        setCurrentVehicle(vehicle);
        const carModel = carModels.find(m => m.id === vehicle.carModelId);
        setSelectedManufacturer(carModel ? carModel.manufacturer : '');
        setSelectedCarModelId(vehicle.carModelId || '');
        setSelectedVehicleSizeId(vehicle.vehicle_size_id || '');
        setComment(vehicle.comment || '');
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    const handleSave = async () => {
        if (!user) return;
        if (settings.is_car_model_required && !selectedCarModelId) {
            alert('차종을 선택해주세요.');
            return;
        }
        try {
            await api.put(`/vehicles/${currentVehicle.id}`, { 
                carModelId: selectedCarModelId || null, 
                comment: comment,
                vehicle_size_id: selectedVehicleSizeId || null
            });
            fetchVehicles(pagination.currentPage);
            handleCloseModal();
        } catch (error) {
            console.error("Error updating vehicle:", error);
        }
    };

    const handleArchive = async (id) => {
        if (!user) return;
        if (window.confirm('정말로 이 차량을 삭제(보관)하시겠습니까? 이 작업은 되돌릴 수 없으며, 같은 번호로 재등록이 가능해집니다.')) {
            try {
                await api.delete(`/vehicles/${id}`);
                fetchVehicles(pagination.currentPage);
            } catch (error) {
                console.error("Error archiving vehicle:", error);
            }
        }
    };

    const renderPagination = () => {
        const { currentPage, totalPages } = pagination;
        if (totalPages <= 1) return null;

        const pageBlockSize = 10;
        const currentBlock = Math.ceil(currentPage / pageBlockSize);
        const startPageInBlock = (currentBlock - 1) * pageBlockSize + 1;
        const endPageInBlock = Math.min(startPageInBlock + pageBlockSize - 1, totalPages);

        const items = [];

        // First Page
        items.push(
            <Pagination.First 
                key="first" 
                onClick={() => handlePageChange(1)} 
                disabled={currentPage === 1} 
            />
        );

        // Previous Block
        items.push(
            <Pagination.Prev 
                key="prev-block" 
                onClick={() => handlePageChange(startPageInBlock - 1)} 
                disabled={startPageInBlock === 1} 
            />
        );

        // Page numbers
        for (let number = startPageInBlock; number <= endPageInBlock; number++) {
            items.push(
                <Pagination.Item key={number} active={number === currentPage} onClick={() => handlePageChange(number)}>
                    {number}
                </Pagination.Item>,
            );
        }

        // Next Block
        items.push(
            <Pagination.Next 
                key="next-block" 
                onClick={() => handlePageChange(endPageInBlock + 1)} 
                disabled={endPageInBlock === totalPages} 
            />
        );

        // Last Page
        items.push(
            <Pagination.Last 
                key="last" 
                onClick={() => handlePageChange(totalPages)} 
                disabled={currentPage === totalPages} 
            />
        );

        return <Pagination className="justify-content-center">{items}</Pagination>;
    };

    return (
        <>
            <div className="page-header">
                <div className="page-header-left">
                    <h3>차량 관리</h3>
                    <p>등록된 차량 정보와 서비스 이력을 관리합니다</p>
                </div>
            </div>
            {/* PC 검색 */}
            <Card className="mb-4 d-none d-md-block">
                <Card.Body>
                    <Form onSubmit={handleSearch}>
                        <Row className="align-items-end">
                            <Col md={5}><Form.Group><Form.Label>차량번호 검색</Form.Label><Form.Control type="text" name="plateNumberSearch" placeholder="차량번호" value={filters.plateNumberSearch} onChange={handleFilterChange} /></Form.Group></Col>
                            <Col md={5}><Form.Group><Form.Label>차종 검색</Form.Label><Form.Control type="text" name="modelSearch" placeholder="제조사 또는 모델명" value={filters.modelSearch} onChange={handleFilterChange} /></Form.Group></Col>
                            <Col md={2} className="d-grid"><Button type="submit" disabled={isLoading}>{isLoading ? <Spinner size="sm" /> : '검색'}</Button></Col>
                        </Row>
                    </Form>
                </Card.Body>
            </Card>

            {/* 모바일 검색 */}
            <div className="d-md-none mb-3">
                <Form onSubmit={handleSearch}>
                    <div className="mob-search-bar">
                        <Form.Control type="text" name="plateNumberSearch" placeholder="차량번호 검색" value={filters.plateNumberSearch} onChange={handleFilterChange} className="mob-search-input" />
                        <Form.Control type="text" name="modelSearch" placeholder="차종 검색" value={filters.modelSearch} onChange={handleFilterChange} className="mob-search-input" />
                        <Button type="submit" className="w-100" disabled={isLoading}>{isLoading ? <Spinner size="sm" /> : '검색'}</Button>
                    </div>
                </Form>
            </div>
            <Card>
                <Card.Body className="p-0">
                    {/* PC 테이블 */}
                    <Table hover responsive className="mb-0 d-none d-md-table">
                        <thead>
                            <tr><th>차량번호</th><th>고객</th><th>차종</th><th>차량 사이즈</th><th>코멘트</th><th>상태</th><th>관리</th></tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan="8" className="text-center"><Spinner animation="border" /></td></tr>
                            ) : vehicles.map(vehicle => (
                                <Fragment key={vehicle.id}>
                                    <tr onClick={() => toggleRow(vehicle.id, vehicle.plateNumber)} style={{ cursor: 'pointer' }}>
                                        <td>
                                            <span className="fw-semibold" style={{ color: 'var(--color-primary)', letterSpacing: '0.03em' }}>{vehicle.plateNumber}</span>
                                        </td>
                                        <td>
                                            {vehicle.customer_id ? (
                                                <Link to={`/admin/customers/${vehicle.customer_id}`} onClick={e => e.stopPropagation()} className="text-decoration-none">
                                                    {vehicle.customerName} →
                                                </Link>
                                            ) : <span className="text-muted">-</span>}
                                        </td>
                                        <td>{vehicle.manufacturer || vehicle.modelName ? `${vehicle.manufacturer || ''} ${vehicle.modelName || ''}`.trim() : <span className="text-muted">차종 미지정</span>}</td>
                                        <td>{vehicle.sizeName || <span className="text-muted">-</span>}</td>
                                        <td className="text-muted" style={{ maxWidth: 160, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={vehicle.comment || ''}>{vehicle.comment || '-'}</td>
                                        <td>{vehicle.isActive ? <Badge bg="success">활성</Badge> : <Badge bg="secondary">보관</Badge>}</td>
                                        <td>
                                            {vehicle.isActive && (<>
                                                {hasPermission(user?.role, 'vehicles:edit') && (
                                                    <Button variant="info" size="sm" onClick={(e) => { e.stopPropagation(); handleShowModal(vehicle); }}>정보 변경</Button>
                                                )}{' '}
                                                {hasPermission(user?.role, 'vehicles:delete') && (
                                                    <Button variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); handleArchive(vehicle.id); }}>삭제(보관)</Button>
                                                )}
                                            </>)}
                                        </td>
                                    </tr>
                                    <Collapse in={expandedRows[vehicle.id]}>
                                        <tr>
                                            <td colSpan="8" className="p-0">
                                                <div className="p-3" style={{background: 'var(--color-bg)'}}>
                                                {serviceHistories[vehicle.id] === 'loading' ? <Spinner size="sm" /> :
                                                 serviceHistories[vehicle.id] && serviceHistories[vehicle.id].length > 0 ? (
                                                    <Table hover responsive size="sm">
                                                        <thead><tr><th>서비스 날짜</th><th>메뉴</th><th>옵션</th><th>가격</th><th>코멘트</th></tr></thead>
                                                        <tbody>
                                                            {serviceHistories[vehicle.id].map(history => (
                                                                <tr key={history.id}>
                                                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(history.startTime || history.created_at)}</td>
                                                                    <td>{history.menuName}</td>
                                                                    <td>{history.selectedOptions || '-'}</td>
                                                                    <td>{parseInt(history.finalPrice || 0).toLocaleString()}원</td>
                                                                    <td style={{ maxWidth: 160, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }} title={history.comment || ''}>{history.comment || '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                    ) : <p className="text-center mb-0">서비스 내역이 없습니다.</p>
                                                }
                                                </div>
                                            </td>
                                        </tr>
                                    </Collapse>
                                </Fragment>
                            ))}
                        </tbody>
                    </Table>

                    {/* 모바일 카드 */}
                    <div className="d-md-none mob-list">
                        {isLoading ? (
                            <div className="text-center py-4"><Spinner size="sm" /> <span className="text-muted ms-2">불러오는 중...</span></div>
                        ) : vehicles.map(vehicle => {
                            const isExpanded = !!expandedRows[vehicle.id];
                            const history = serviceHistories[vehicle.id];
                            return (
                            <div key={vehicle.id} className={`mob-card ${isExpanded ? 'mob-card-expanded' : ''}`} onClick={() => toggleRow(vehicle.id, vehicle.plateNumber)} role="button">
                                {/* 1행: 차량번호 + 상태 + 펼침 표시 */}
                                <div className="mob-card-row1">
                                    <span className="mob-vh-plate">{vehicle.plateNumber}</span>
                                    <span className="d-flex align-items-center gap-2">
                                        {vehicle.isActive ? <Badge bg="success" className="mob-status-badge">활성</Badge> : <Badge bg="secondary" className="mob-status-badge">보관</Badge>}
                                        <span className="mob-card-chevron" aria-hidden>{isExpanded ? '▴' : '▾'}</span>
                                    </span>
                                </div>
                                {/* 2행: 차종 + 사이즈 */}
                                <div className="mob-card-row2">
                                    {vehicle.manufacturer || vehicle.modelName
                                        ? <><span>{vehicle.manufacturer || ''} {vehicle.modelName || ''}</span>{vehicle.sizeName && <><span className="mob-card-sep">·</span><span>{vehicle.sizeName}</span></>}</>
                                        : <span className="text-muted">차종 미지정</span>
                                    }
                                </div>
                                {/* 고객 */}
                                {vehicle.customer_id && (
                                    <div className="mob-card-customer">
                                        <Link to={`/admin/customers/${vehicle.customer_id}`} onClick={e => e.stopPropagation()} className="mob-customer-link">
                                            👤 {vehicle.customerName}
                                        </Link>
                                    </div>
                                )}
                                {/* 코멘트 */}
                                {vehicle.comment && <div className="mob-card-comment">💬 {vehicle.comment}</div>}
                                {/* 액션 */}
                                {vehicle.isActive && (
                                    <div className="mob-card-actions">
                                        {hasPermission(user?.role, 'vehicles:edit') && (
                                            <button className="mob-action-btn" onClick={(e) => { e.stopPropagation(); handleShowModal(vehicle); }}>정보 변경</button>
                                        )}
                                        {hasPermission(user?.role, 'vehicles:delete') && (
                                            <button className="mob-action-btn mob-action-danger" onClick={(e) => { e.stopPropagation(); handleArchive(vehicle.id); }}>삭제(보관)</button>
                                        )}
                                    </div>
                                )}
                                {/* 서비스 이력 (탭 펼침) */}
                                {isExpanded && (
                                    <div className="mob-card-history" onClick={e => e.stopPropagation()}>
                                        <div className="mob-card-history-title">서비스 이력</div>
                                        {history === 'loading' ? (
                                            <div className="text-center py-2"><Spinner size="sm" /></div>
                                        ) : history && history.length > 0 ? (
                                            <ul className="mob-history-list">
                                                {history.map(h => (
                                                    <li key={h.id} className="mob-history-item">
                                                        <div className="mob-history-row1">
                                                            <span className="mob-history-date">{formatDateTime(h.startTime || h.created_at)}</span>
                                                            <span className="mob-history-price">{parseInt(h.finalPrice || 0).toLocaleString()}원</span>
                                                        </div>
                                                        <div className="mob-history-row2">
                                                            <span className="mob-history-menu">{h.menuName || '-'}</span>
                                                            {h.selectedOptions && <span className="mob-history-opts">+ {h.selectedOptions}</span>}
                                                        </div>
                                                        {h.comment && <div className="mob-history-comment">💬 {h.comment}</div>}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-center text-muted py-2">서비스 내역이 없습니다.</div>
                                        )}
                                    </div>
                                )}
                            </div>
                            );
                        })}
                    </div>

                    <div className="px-3 pb-3">{renderPagination()}</div>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal}>
                <Modal.Header closeButton><Modal.Title>차량 정보 수정: {currentVehicle.plateNumber}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3"><Form.Label>제조사 선택</Form.Label><Form.Select value={selectedManufacturer} onChange={e => { setSelectedManufacturer(e.target.value); setSelectedCarModelId(''); }}><option value="">-- 제조사를 선택하세요 --</option>{uniqueManufacturers.map(m => (<option key={m} value={m}>{m}</option>))}</Form.Select></Form.Group>
                        <Form.Group className="mb-3"><Form.Label>모델명 선택</Form.Label><Form.Select value={selectedCarModelId} onChange={e => setSelectedCarModelId(e.target.value)} disabled={!selectedManufacturer}><option value="">-- 모델명을 선택하세요 --</option>{modelsByManufacturer.map(model => (<option key={model.id} value={model.id}>{model.modelName} ({model.vehicleClass})</option>))}</Form.Select></Form.Group>
                        <Form.Group className="mb-3"><Form.Label>차량 사이즈 선택</Form.Label><Form.Select value={selectedVehicleSizeId} onChange={e => setSelectedVehicleSizeId(e.target.value)}><option value="">-- 사이즈를 선택하세요 --</option>{vehicleSizes.map(size => (<option key={size.id} value={size.id}>{size.sizeName}</option>))}</Form.Select></Form.Group>
                        <Form.Group className="mb-3"><Form.Label>차량 코멘트</Form.Label><Form.Control as="textarea" rows={3} name="comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="차량에 대한 코멘트를 입력하세요." /></Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={handleCloseModal}>닫기</Button><Button variant="primary" onClick={handleSave}>저장</Button></Modal.Footer>
            </Modal>
        </>
    );
};

export default ManageVehicles;
